import { useCallback, useEffect, useState } from 'react'

import { Modal } from '@trybeapp/ui.modal'
import styled, { css } from '@xstyled/styled-components'
import { Button } from '@trybeapp/ui.button'
import {
  DEFAULT_STAGE,
  TIMES_STAGE,
  useAddAppointmentOrderItemOverlay,
  useStage,
  ALL_STAGES,
  useNewItem,
} from './contexts'
import { Text } from 'components/Text'
import { Box } from '@trybeapp/ui.box'
import { FontAwesomeIcon } from 'components/FontAwesomeIcon'
import { DefaultStage } from './stages/Default'
import { TimesStage } from './stages/Times'
import { useIsEditMode } from './hooks'
import { AppointmentTypeProvider } from 'contexts/AppointmentType'
import { useFormikContext } from 'formik'
import { useGetAppointmentType } from 'api/AppointmentTypes'

/**
 * @deprecated
 */
export const AddAppointmentOrderItemOverlay = () => {
  const {
    modal: { visible },
  } = useAddAppointmentOrderItemOverlay()
  const { setNewItem } = useNewItem()

  useEffect(
    () => () => {
      if (visible) {
        setNewItem({})
      }
    },
    [setNewItem, visible]
  )

  return visible && <AddAppointmentOrderItemOverlayWrapper />
}

const AddAppointmentOrderItemOverlayWrapper = () => {
  const { modal } = useAddAppointmentOrderItemOverlay()
  const { stage } = useStage()
  const isEditMode = useIsEditMode()
  const {
    newItem: { type_id: typeId },
  } = useNewItem()
  const [size, setSize] = useState('md')

  const { data: { data: appointmentType = {} } = {} } = useGetAppointmentType(typeId, {
    enabled: !!typeId,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  })

  useEffect(() => {
    switch (stage) {
      case TIMES_STAGE:
        setSize('auto')
        break
      default:
      case DEFAULT_STAGE:
        setSize('md')
    }
  }, [stage])

  const StageComponent = useCallback(() => {
    switch (stage) {
      case TIMES_STAGE:
        return (
          <AppointmentTypeProvider appointmentType={appointmentType}>
            <TimesStage />
          </AppointmentTypeProvider>
        )
      default:
      case DEFAULT_STAGE:
        return <DefaultStage />
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appointmentType?.id, stage])

  return (
    <Modal
      {...modal}
      size={size}
      ariaLabel={`${isEditMode ? 'Edit' : 'Add'}`}
      hideOnClickOutside={false}
    >
      <StageComponent />
    </Modal>
  )
}

export const SelectPill = ({ selected, disabled, onClick, children }) => {
  const color = disabled ? 'nude.300' : selected ? 'nude.900' : 'nude.700'

  const handleOnClick = (event) => {
    if (disabled) {
      return
    }

    onClick(event)
  }

  return (
    <StyledBox
      borderRadius="sm"
      selected={selected}
      disabled={disabled}
      backgroundColor={selected ? 'light.500' : 'light.700'}
      p="sm"
      style={{ cursor: disabled ? 'not-allowed' : 'pointer' }}
      display="flex"
      alignItems="center"
      onClick={handleOnClick}
      color={color}
      borderColor="light.500"
      borderStyle="solid"
      borderWidth={selected ? 'sm' : 'sm'}
      mb="sm"
    >
      <FontAwesomeIcon
        icon={selected ? 'check-square' : ['far', 'square']}
        fontSize="body2"
        color="inherit"
        mr="sm"
        fixedWidth
      />

      <Text color="inherit" variant="body2" alignItems="center">
        {children}
      </Text>
    </StyledBox>
  )
}

export const NextButton = () => {
  const { submitForm, isValid, dirty } = useFormikContext()
  const { id: basketItemId } = useNewItem()
  const isNew = !basketItemId

  return (
    <Button variant="primary" onClick={submitForm} disabled={!isValid || (!dirty && !isNew)}>
      Next
    </Button>
  )
}

export const BackButton = () => {
  const { setStage, stage } = useStage()
  const previousStageIndex = ALL_STAGES.findIndex((currentStage) => stage === currentStage) - 1
  const handleOnClick = () => setStage(ALL_STAGES[previousStageIndex])

  return (
    <Button variant="ghost" mx="sm" onClick={handleOnClick}>
      Back
    </Button>
  )
}

const StyledBox = styled(Box)(
  ({ selected = false, disabled = false }) => css`
    &:hover {
      ${disabled
        ? css`
            background-color: light.700;
          `
        : selected
        ? css`
            background-color: light.200;
          `
        : css`
            background-color: light.500;
            border-width: xs;
          `}
    }
  `
)
